import { useCallback } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * Spec 062 — `offers.*` strings for the partner Promotions screens.
 *
 * The portal's `t()` reads translations from the admin DB (platform service). Until the
 * `offers.*` keys are seeded there, these bundled EN/AR strings are used; a DB value for
 * the same key always wins, so admins can still edit copy without a deploy.
 */
type Dict = Record<string, string>;

const en: Dict = {
  'offers.title': 'Promotions',
  'offers.subtitle': 'Run time-limited discounts on your services. Garagat reviews every offer before it goes live.',
  'offers.new': 'New offer',
  'offers.loading': 'Loading offers…',
  'offers.loadFailed': 'We could not load your offers. Please try again.',
  'offers.retry': 'Try again',
  'offers.empty.title': 'Run your first offer',
  'offers.empty.body': 'Bring in more bookings with a discount you control.',
  'offers.empty.rule1': 'Garagat reviews every offer within 1 business day.',
  'offers.empty.rule2': 'You fund the discount: customers pay the offer price and your payout is based on it.',
  'offers.empty.rule3': 'An offer runs for up to 90 days.',
  'offers.filter.label': 'Status',
  'offers.filter.all': 'All',
  'offers.filter.ARCHIVED': 'Archived',
  'offers.filter.empty': 'No offers with this status.',
  'offers.col.offer': 'Offer',
  'offers.col.status': 'Status',
  'offers.col.window': 'Runs',
  'offers.col.services': 'Services',
  'offers.col.budget': 'Bookings used',
  'offers.col.results': 'Results',
  'offers.col.actions': 'Actions',
  'offers.itemsCount': '{n} services',
  'offers.itemsCountOne': '1 service',
  'offers.budget.unlimited': 'no limit',
  'offers.results': '{views} views · {bookings} bookings · {discount} given',
  'offers.status.DRAFT': 'Draft',
  'offers.status.PENDING_APPROVAL': 'Pending approval',
  'offers.status.SCHEDULED': 'Scheduled',
  'offers.status.LIVE': 'Live',
  'offers.status.PAUSED': 'Paused',
  'offers.status.ENDED': 'Ended',
  'offers.status.REJECTED': 'Rejected',
  'offers.status.SUSPENDED': 'Suspended',
  'offers.hidden.GARAGE_BLOCKED': 'Hidden: garage blocked',
  'offers.hidden.NO_BOOKABLE_SERVICE': 'Hidden: no bookable service',
  'offers.pendingRevision': 'Changes pending approval',
  'offers.adminEdited': 'Last edited by Garagat on {date}',
  'offers.action.edit': 'Edit',
  'offers.action.view': 'View',
  'offers.action.pause': 'Pause',
  'offers.action.resume': 'Resume',
  'offers.action.end': 'End now',
  'offers.action.archive': 'Archive',
  'offers.action.withdraw': 'Withdraw',
  'offers.confirm.cancel': 'Cancel',
  'offers.confirm.pause.title': 'Pause this offer?',
  'offers.confirm.pause.body': 'Customers stop seeing it within a minute. You can resume it any time before it ends.',
  'offers.confirm.resume.title': 'Resume this offer?',
  'offers.confirm.resume.body': 'Customers will see the offer again within a minute.',
  'offers.confirm.end.title': 'End this offer now?',
  'offers.confirm.end.body': 'The offer stops immediately and cannot be restarted. Bookings already made keep their price.',
  'offers.confirm.archive.title': 'Archive this offer?',
  'offers.confirm.archive.body': 'It moves out of your list. You can still find it under the Archived filter.',
  'offers.confirm.withdraw.title': 'Withdraw from review?',
  'offers.confirm.withdraw.body': 'The offer goes back to draft so you can change it and submit again.',
  'offers.toast.updated': 'Offer updated',
  'offers.editor.newTitle': 'New offer',
  'offers.editor.editTitle': 'Edit offer',
  'offers.editor.back': 'Back to promotions',
  'offers.editor.loadFailed': 'We could not load this offer.',
  'offers.step.details': 'Details',
  'offers.step.services': 'Services & discount',
  'offers.step.banner': 'Banner',
  'offers.step.preview': 'Preview & submit',
  'offers.field.titleEn': 'Title (English)',
  'offers.field.titleAr': 'Title (Arabic)',
  'offers.field.subtitleEn': 'Subtitle (English)',
  'offers.field.subtitleAr': 'Subtitle (Arabic)',
  'offers.field.startsAt': 'Starts on',
  'offers.field.endsAt': 'Ends on',
  'offers.field.services': 'Services in this offer',
  'offers.field.discountType': 'Discount type',
  'offers.field.percent': 'Percentage (%)',
  'offers.field.fixed': 'Fixed amount (AED)',
  'offers.field.discountValuePercent': 'Discount (%)',
  'offers.field.discountValueFixed': 'Discount (AED)',
  'offers.field.maxDiscountPerBooking': 'Max discount per booking (AED)',
  'offers.field.maxBookings': 'Max bookings',
  'offers.field.optional': 'optional',
  'offers.hint.window': 'An offer can run for up to 90 days.',
  'offers.hint.percent': 'Between 5% and 70%.',
  'offers.hint.fixed': 'At least AED 5 and at least AED 1 below each service’s price.',
  'offers.field.defaultDiscountPercent': 'Default discount (%)',
  'offers.field.defaultDiscountFixed': 'Default discount (AED)',
  'offers.hint.defaultDiscount': 'Fills in each service you tick. Set each service’s own discount in the list below.',
  'offers.action.applyToAll': 'Apply to all',
  'offers.action.applyToAllHint': 'Copy the default discount to every ticked service',
  'offers.services.valueFor': 'Discount for {name}',
  'offers.services.discountCol': 'Discount',
  'offers.err.exceedsServicePrice': 'Must be at least AED 1 below this service’s price.',
  'offers.hint.maxDiscountPerBooking': 'Caps the saving on expensive services. Leave blank for no cap.',
  'offers.hint.maxBookings': 'The offer ends when this many bookings use it. Leave blank for no limit.',
  'offers.hint.arabic': 'Optional. Customers using Arabic see the English title when this is empty.',
  'offers.services.loading': 'Loading your services…',
  'offers.services.loadFailed': 'We could not load your services.',
  'offers.services.empty': 'You have no active services yet. Add one under Services first.',
  'offers.services.selected': '{n} selected',
  'offers.services.listPrice': 'List price',
  'offers.services.offerPrice': 'Offer price',
  'offers.services.from': 'from',
  'offers.services.previewNote': 'Preview only. Garagat calculates the final price at checkout.',
  'offers.variants.title': 'Prices per variant',
  'offers.variants.intro': 'Each service’s discount applies to all of its variants. These are the prices customers will pay.',
  'offers.variants.needRule': 'Choose a service and enter a discount to see the price of every variant.',
  'offers.variants.loading': 'Calculating prices…',
  'offers.variants.failed': 'We could not calculate the prices per variant. Change the discount to try again.',
  'offers.variants.summary': '{n} prices · up to {pct}% off',
  'offers.variants.summaryOne': '1 price · {pct}% off',
  'offers.variants.skippedSummary': '{n} with no discount',
  'offers.variants.col.variant': 'Variant',
  'offers.variants.col.before': 'Before',
  'offers.variants.col.after': 'After',
  'offers.variants.col.saving': 'Saving',
  'offers.variants.standard': 'Standard price',
  'offers.variants.skip.BELOW_MIN_PRICE': 'No discount: it would leave less than AED 1',
  'offers.variants.skip.NO_DISCOUNT': 'No discount',
  'offers.variants.showAll': 'Show all {n}',
  'offers.variants.showFewer': 'Show fewer',
  'offers.variants.notBookable': 'Customers can’t book this service right now, so the offer won’t show on it.',
  'offers.variants.noPrices': 'No price yet',
  'offers.banner.en': 'Banner (English)',
  'offers.banner.ar': 'Banner (Arabic)',
  'offers.banner.drop': 'Drop a JPG, PNG or WebP here, or click to choose',
  'offers.banner.spec': '2:1 ratio, at least 1200×600 pixels, up to 5 MB. We crop the centre to 2:1.',
  'offers.banner.arHint': 'Optional. Without it, the English banner is shown with Arabic text.',
  'offers.banner.uploading': 'Uploading… {p}%',
  'offers.banner.processing': 'Preparing image…',
  'offers.banner.remove': 'Remove',
  'offers.banner.replace': 'Replace',
  'offers.banner.alt': 'Offer banner',
  'offers.banner.err.type': 'Use a JPG, PNG or WebP image.',
  'offers.banner.err.size': 'The image is larger than 5 MB.',
  'offers.banner.err.dimensions': 'The image must be at least 1200×600 pixels.',
  'offers.banner.err.read': 'We could not read this image. Try another file.',
  'offers.banner.uploadFailed': 'Upload failed, try again',
  'offers.banner.suggested.title': 'Suggested banners',
  'offers.banner.suggested.hint': 'No design yet? Pick one of ours, or upload your own above.',
  'offers.banner.suggested.pick': 'Use banner: {name}',
  'offers.banner.template.navyYellow': 'Navy & yellow',
  'offers.banner.template.teal': 'Teal',
  'offers.banner.template.charcoalOrange': 'Charcoal & orange',
  'offers.banner.template.green': 'Green',
  'offers.banner.template.burgundy': 'Burgundy',
  'offers.preview.intro': 'This is how customers see your offer in the Garagat app.',
  'offers.preview.language': 'Language',
  'offers.preview.theme': 'Theme',
  'offers.preview.en': 'English',
  'offers.preview.ar': 'العربية',
  'offers.preview.light': 'Light',
  'offers.preview.dark': 'Dark',
  'offers.preview.summary': 'Summary',
  'offers.bar.saveDraft': 'Save draft',
  'offers.bar.saveChanges': 'Save changes',
  'offers.bar.submit': 'Submit for approval',
  'offers.bar.submitChanges': 'Submit changes for approval',
  'offers.bar.withdraw': 'Withdraw',
  'offers.bar.next': 'Next',
  'offers.bar.back': 'Back',
  'offers.bar.saving': 'Saving…',
  'offers.bar.saved': 'Draft saved',
  'offers.bar.changesSaved': 'Changes saved',
  'offers.bar.submitted': 'Submitted for approval',
  'offers.bar.withdrawn': 'Withdrawn. The offer is a draft again.',
  'offers.notice.rejected': 'Garagat did not approve this offer',
  'offers.notice.rejectedHint': 'Fix the points below and submit again.',
  'offers.notice.suspended': 'Garagat suspended this offer',
  'offers.notice.suspendedHint': 'Customers cannot see it. Edit it and submit again.',
  'offers.notice.pending': 'Waiting for Garagat approval. Withdraw it if you need to make changes.',
  'offers.notice.running': 'This offer is {status}. Changes to its content go to Garagat for approval while the current version keeps running. Shortening the end date or lowering max bookings applies at once.',
  'offers.notice.pendingRevision': 'Your changes are waiting for approval. Customers still see the current version.',
  'offers.notice.revisionRejected': 'Garagat did not approve your latest changes.',
  'offers.notice.ended': 'This offer has ended. It is kept for your records.',
  'offers.reason.CONTACT_DETAILS': 'Contact details or an off-platform link on the banner',
  'offers.reason.PRICE_MISMATCH': 'The price on the banner differs from the offer',
  'offers.reason.UNREADABLE_IMAGE': 'Unreadable or low-quality image',
  'offers.reason.MISLEADING_CLAIM': 'Misleading claim',
  'offers.reason.COMPETITOR_BRANDING': 'Competitor branding',
  'offers.reason.ARABIC_MISSING': 'Arabic text is missing',
  'offers.reason.OTHER': 'Other',
  'offers.err.titleRequired': 'Enter an English title.',
  'offers.err.titleTooLong': 'Keep the title to 60 characters.',
  'offers.err.subtitleTooLong': 'Keep the subtitle to 90 characters.',
  'offers.err.startRequired': 'Choose a start date.',
  'offers.err.endRequired': 'Choose an end date.',
  'offers.err.startPast': 'The offer cannot start before today.',
  'offers.err.endBeforeStart': 'The end date must be after the start date.',
  'offers.err.window': 'An offer can run for up to 90 days.',
  'offers.err.percentRange': 'Discount must be between 5% and 70%.',
  'offers.err.fixedMin': 'A fixed discount must be at least AED 5.',
  'offers.err.exceedsPrice': 'Fixed discount must be lower than the cheapest covered service.',
  'offers.err.maxPerBookingMin': 'Use at least AED 5, or leave it blank.',
  'offers.err.maxBookingsRange': 'Use a whole number from 1 to 10,000, or leave it blank.',
  'offers.err.noService': 'Choose at least one active service.',
  'offers.err.bannerRequired': 'Add a banner image (JPG, PNG or WebP, up to 5 MB).',
  'offers.err.contactDetails': 'Contact details and links are not allowed in offers.',
  'offers.err.arabicRequired': 'An Arabic title is required.',
  'offers.err.maxBelowUsed': 'Max bookings cannot be lower than the bookings already used.',
  'offers.err.garageBlocked': 'Your garage must be approved and active to run offers.',
  'offers.err.transition': 'This offer cannot do that in its current status. Reload to see the latest.',
  'offers.err.stateChanged': 'This offer changed, reload and try again.',
  'offers.err.ended': 'This offer has already ended.',
  'offers.err.featureDisabled': 'Offers are temporarily disabled.',
  'offers.err.storageKey': 'The banner upload could not be verified. Upload it again.',
  'offers.err.generic': 'Something went wrong. Please try again.',
  'offers.err.reload': 'Reload',
  'offers.err.fixBelow': 'Please fix the highlighted fields.',
  // #995: a service price under a running offer's minimum (shown on the service price field)
  'offers.priceGuard.message': "This price is covered by the offer '{title}' ({off}). Minimum price while the offer runs: AED {min}. End or edit the offer first.",
  'offers.priceGuard.offFixed': 'AED {amount} off',
  'offers.priceGuard.offPercent': '{pct}% off',
};

const ar: Dict = {
  'offers.title': 'العروض',
  'offers.subtitle': 'قدّم خصومات لفترة محدودة على خدماتك. تراجع كراجات كل عرض قبل نشره.',
  'offers.new': 'عرض جديد',
  'offers.loading': 'جارٍ تحميل العروض…',
  'offers.loadFailed': 'تعذّر تحميل عروضك. حاول مرة أخرى.',
  'offers.retry': 'إعادة المحاولة',
  'offers.empty.title': 'أطلق عرضك الأول',
  'offers.empty.body': 'احصل على حجوزات أكثر بخصم تتحكم فيه بنفسك.',
  'offers.empty.rule1': 'تراجع كراجات كل عرض خلال يوم عمل واحد.',
  'offers.empty.rule2': 'أنت من يتحمّل الخصم: يدفع العميل سعر العرض وتُحتسب مستحقاتك على أساسه.',
  'offers.empty.rule3': 'تصل مدة العرض إلى 90 يومًا كحد أقصى.',
  'offers.filter.label': 'الحالة',
  'offers.filter.all': 'الكل',
  'offers.filter.ARCHIVED': 'المؤرشفة',
  'offers.filter.empty': 'لا توجد عروض بهذه الحالة.',
  'offers.col.offer': 'العرض',
  'offers.col.status': 'الحالة',
  'offers.col.window': 'المدة',
  'offers.col.services': 'الخدمات',
  'offers.col.budget': 'الحجوزات المستخدمة',
  'offers.col.results': 'النتائج',
  'offers.col.actions': 'الإجراءات',
  'offers.itemsCount': '{n} خدمات',
  'offers.itemsCountOne': 'خدمة واحدة',
  'offers.budget.unlimited': 'بلا حد',
  'offers.results': '{views} مشاهدة · {bookings} حجز · خصومات {discount}',
  'offers.status.DRAFT': 'مسودة',
  'offers.status.PENDING_APPROVAL': 'بانتظار الموافقة',
  'offers.status.SCHEDULED': 'مجدول',
  'offers.status.LIVE': 'نشط',
  'offers.status.PAUSED': 'متوقف مؤقتًا',
  'offers.status.ENDED': 'منتهٍ',
  'offers.status.REJECTED': 'مرفوض',
  'offers.status.SUSPENDED': 'معلّق',
  'offers.hidden.GARAGE_BLOCKED': 'مخفي: الكراج محظور',
  'offers.hidden.NO_BOOKABLE_SERVICE': 'مخفي: لا توجد خدمة متاحة للحجز',
  'offers.pendingRevision': 'تعديلات بانتظار الموافقة',
  'offers.adminEdited': 'آخر تعديل من كراجات في {date}',
  'offers.action.edit': 'تعديل',
  'offers.action.view': 'عرض',
  'offers.action.pause': 'إيقاف مؤقت',
  'offers.action.resume': 'استئناف',
  'offers.action.end': 'إنهاء الآن',
  'offers.action.archive': 'أرشفة',
  'offers.action.withdraw': 'سحب',
  'offers.confirm.cancel': 'إلغاء',
  'offers.confirm.pause.title': 'إيقاف هذا العرض مؤقتًا؟',
  'offers.confirm.pause.body': 'سيختفي عن العملاء خلال دقيقة. يمكنك استئنافه في أي وقت قبل انتهائه.',
  'offers.confirm.resume.title': 'استئناف هذا العرض؟',
  'offers.confirm.resume.body': 'سيظهر العرض للعملاء مجددًا خلال دقيقة.',
  'offers.confirm.end.title': 'إنهاء هذا العرض الآن؟',
  'offers.confirm.end.body': 'يتوقف العرض فورًا ولا يمكن إعادة تشغيله. تحتفظ الحجوزات السابقة بسعرها.',
  'offers.confirm.archive.title': 'أرشفة هذا العرض؟',
  'offers.confirm.archive.body': 'سيُنقل خارج قائمتك، ويمكنك إيجاده ضمن فلتر المؤرشفة.',
  'offers.confirm.withdraw.title': 'سحب العرض من المراجعة؟',
  'offers.confirm.withdraw.body': 'سيعود العرض مسودة لتتمكن من تعديله وإرساله من جديد.',
  'offers.toast.updated': 'تم تحديث العرض',
  'offers.editor.newTitle': 'عرض جديد',
  'offers.editor.editTitle': 'تعديل العرض',
  'offers.editor.back': 'العودة إلى العروض',
  'offers.editor.loadFailed': 'تعذّر تحميل هذا العرض.',
  'offers.step.details': 'التفاصيل',
  'offers.step.services': 'الخدمات والخصم',
  'offers.step.banner': 'صورة العرض',
  'offers.step.preview': 'المعاينة والإرسال',
  'offers.field.titleEn': 'العنوان (بالإنجليزية)',
  'offers.field.titleAr': 'العنوان (بالعربية)',
  'offers.field.subtitleEn': 'العنوان الفرعي (بالإنجليزية)',
  'offers.field.subtitleAr': 'العنوان الفرعي (بالعربية)',
  'offers.field.startsAt': 'يبدأ في',
  'offers.field.endsAt': 'ينتهي في',
  'offers.field.services': 'الخدمات المشمولة',
  'offers.field.discountType': 'نوع الخصم',
  'offers.field.percent': 'نسبة مئوية (٪)',
  'offers.field.fixed': 'مبلغ ثابت (درهم)',
  'offers.field.discountValuePercent': 'الخصم (٪)',
  'offers.field.discountValueFixed': 'الخصم (درهم)',
  'offers.field.maxDiscountPerBooking': 'الحد الأقصى للخصم لكل حجز (درهم)',
  'offers.field.maxBookings': 'الحد الأقصى للحجوزات',
  'offers.field.optional': 'اختياري',
  'offers.hint.window': 'تصل مدة العرض إلى 90 يومًا كحد أقصى.',
  'offers.hint.percent': 'بين 5٪ و70٪.',
  'offers.hint.fixed': '5 دراهم على الأقل، وأقل من سعر كل خدمة بدرهم واحد على الأقل.',
  'offers.field.defaultDiscountPercent': 'الخصم الافتراضي (٪)',
  'offers.field.defaultDiscountFixed': 'الخصم الافتراضي (درهم)',
  'offers.hint.defaultDiscount': 'يُملأ تلقائيًا لكل خدمة تختارها. حدّد خصم كل خدمة في القائمة أدناه.',
  'offers.action.applyToAll': 'تطبيق على الكل',
  'offers.action.applyToAllHint': 'نسخ الخصم الافتراضي إلى كل الخدمات المختارة',
  'offers.services.valueFor': 'الخصم على {name}',
  'offers.services.discountCol': 'الخصم',
  'offers.err.exceedsServicePrice': 'يجب أن يقل عن سعر هذه الخدمة بدرهم واحد على الأقل.',
  'offers.hint.maxDiscountPerBooking': 'يحدّ من قيمة الخصم على الخدمات المرتفعة السعر. اتركه فارغًا لعدم التقييد.',
  'offers.hint.maxBookings': 'ينتهي العرض عند الوصول إلى هذا العدد من الحجوزات. اتركه فارغًا لعدم التقييد.',
  'offers.hint.arabic': 'اختياري. إذا تُرك فارغًا يرى مستخدمو العربية العنوان الإنجليزي.',
  'offers.services.loading': 'جارٍ تحميل خدماتك…',
  'offers.services.loadFailed': 'تعذّر تحميل خدماتك.',
  'offers.services.empty': 'لا توجد لديك خدمات نشطة بعد. أضف خدمة من صفحة الخدمات أولًا.',
  'offers.services.selected': 'تم اختيار {n}',
  'offers.services.listPrice': 'السعر الأصلي',
  'offers.services.offerPrice': 'سعر العرض',
  'offers.services.from': 'ابتداءً من',
  'offers.services.previewNote': 'للمعاينة فقط. تحتسب كراجات السعر النهائي عند الدفع.',
  'offers.variants.title': 'الأسعار لكل خيار',
  'offers.variants.intro': 'يُطبَّق خصم كل خدمة على جميع خياراتها. هذه هي الأسعار التي سيدفعها العملاء.',
  'offers.variants.needRule': 'اختر خدمة وأدخل الخصم لرؤية سعر كل خيار.',
  'offers.variants.loading': 'جارٍ حساب الأسعار…',
  'offers.variants.failed': 'تعذّر حساب الأسعار لكل خيار. غيّر الخصم للمحاولة مرة أخرى.',
  'offers.variants.summary': '{n} أسعار · خصم حتى {pct}٪',
  'offers.variants.summaryOne': 'سعر واحد · خصم {pct}٪',
  'offers.variants.skippedSummary': '{n} بدون خصم',
  'offers.variants.col.variant': 'الخيار',
  'offers.variants.col.before': 'قبل',
  'offers.variants.col.after': 'بعد',
  'offers.variants.col.saving': 'التوفير',
  'offers.variants.standard': 'السعر الأساسي',
  'offers.variants.skip.BELOW_MIN_PRICE': 'بدون خصم: سيصبح السعر أقل من درهم واحد',
  'offers.variants.skip.NO_DISCOUNT': 'بدون خصم',
  'offers.variants.showAll': 'عرض الكل ({n})',
  'offers.variants.showFewer': 'عرض أقل',
  'offers.variants.notBookable': 'لا يمكن للعملاء حجز هذه الخدمة حاليًا، لذلك لن يظهر العرض عليها.',
  'offers.variants.noPrices': 'لا يوجد سعر بعد',
  'offers.banner.en': 'صورة العرض (بالإنجليزية)',
  'offers.banner.ar': 'صورة العرض (بالعربية)',
  'offers.banner.drop': 'اسحب صورة JPG أو PNG أو WebP هنا، أو انقر للاختيار',
  'offers.banner.spec': 'نسبة 2:1، بدقة 1200×600 بكسل على الأقل، وحجم حتى 5 ميغابايت. نقصّ المنتصف بنسبة 2:1.',
  'offers.banner.arHint': 'اختياري. بدونها تُعرض الصورة الإنجليزية مع النص العربي.',
  'offers.banner.uploading': 'جارٍ الرفع… {p}٪',
  'offers.banner.processing': 'جارٍ تجهيز الصورة…',
  'offers.banner.remove': 'إزالة',
  'offers.banner.replace': 'استبدال',
  'offers.banner.alt': 'صورة العرض',
  'offers.banner.err.type': 'استخدم صورة بصيغة JPG أو PNG أو WebP.',
  'offers.banner.err.size': 'حجم الصورة أكبر من 5 ميغابايت.',
  'offers.banner.err.dimensions': 'يجب ألا تقل دقة الصورة عن 1200×600 بكسل.',
  'offers.banner.err.read': 'تعذّرت قراءة هذه الصورة. جرّب ملفًا آخر.',
  'offers.banner.uploadFailed': 'فشل الرفع، حاول مرة أخرى',
  'offers.banner.suggested.title': 'تصاميم مقترحة',
  'offers.banner.suggested.hint': 'ليس لديك تصميم؟ اختر أحد تصاميمنا، أو ارفع تصميمك في الأعلى.',
  'offers.banner.suggested.pick': 'استخدام التصميم: {name}',
  'offers.banner.template.navyYellow': 'كحلي وأصفر',
  'offers.banner.template.teal': 'أخضر مزرق',
  'offers.banner.template.charcoalOrange': 'فحمي وبرتقالي',
  'offers.banner.template.green': 'أخضر',
  'offers.banner.template.burgundy': 'عنابي',
  'offers.preview.intro': 'هكذا يرى العملاء عرضك في تطبيق كراجات.',
  'offers.preview.language': 'اللغة',
  'offers.preview.theme': 'المظهر',
  'offers.preview.en': 'English',
  'offers.preview.ar': 'العربية',
  'offers.preview.light': 'فاتح',
  'offers.preview.dark': 'داكن',
  'offers.preview.summary': 'الملخص',
  'offers.bar.saveDraft': 'حفظ المسودة',
  'offers.bar.saveChanges': 'حفظ التعديلات',
  'offers.bar.submit': 'إرسال للموافقة',
  'offers.bar.submitChanges': 'إرسال التعديلات للموافقة',
  'offers.bar.withdraw': 'سحب',
  'offers.bar.next': 'التالي',
  'offers.bar.back': 'السابق',
  'offers.bar.saving': 'جارٍ الحفظ…',
  'offers.bar.saved': 'تم حفظ المسودة',
  'offers.bar.changesSaved': 'تم حفظ التعديلات',
  'offers.bar.submitted': 'تم الإرسال للموافقة',
  'offers.bar.withdrawn': 'تم السحب. عاد العرض مسودة.',
  'offers.notice.rejected': 'لم توافق كراجات على هذا العرض',
  'offers.notice.rejectedHint': 'عالج الملاحظات أدناه ثم أرسل العرض مجددًا.',
  'offers.notice.suspended': 'علّقت كراجات هذا العرض',
  'offers.notice.suspendedHint': 'العرض غير ظاهر للعملاء. عدّله ثم أرسله مجددًا.',
  'offers.notice.pending': 'بانتظار موافقة كراجات. اسحب العرض إذا احتجت إلى تعديله.',
  'offers.notice.running': 'حالة هذا العرض: {status}. تُرسل تعديلات المحتوى إلى كراجات للموافقة بينما تستمر النسخة الحالية. أما تقديم تاريخ الانتهاء أو خفض الحد الأقصى للحجوزات فيُطبّق فورًا.',
  'offers.notice.pendingRevision': 'تعديلاتك بانتظار الموافقة. لا يزال العملاء يرون النسخة الحالية.',
  'offers.notice.revisionRejected': 'لم توافق كراجات على تعديلاتك الأخيرة.',
  'offers.notice.ended': 'انتهى هذا العرض، ويُحتفظ به في سجلاتك.',
  'offers.reason.CONTACT_DETAILS': 'بيانات تواصل أو رابط خارجي على الصورة',
  'offers.reason.PRICE_MISMATCH': 'السعر على الصورة يختلف عن سعر العرض',
  'offers.reason.UNREADABLE_IMAGE': 'صورة غير واضحة أو منخفضة الجودة',
  'offers.reason.MISLEADING_CLAIM': 'ادعاء مضلل',
  'offers.reason.COMPETITOR_BRANDING': 'علامة تجارية لمنافس',
  'offers.reason.ARABIC_MISSING': 'النص العربي غير موجود',
  'offers.reason.OTHER': 'سبب آخر',
  'offers.err.titleRequired': 'أدخل عنوانًا بالإنجليزية.',
  'offers.err.titleTooLong': 'يجب ألا يتجاوز العنوان 60 حرفًا.',
  'offers.err.subtitleTooLong': 'يجب ألا يتجاوز العنوان الفرعي 90 حرفًا.',
  'offers.err.startRequired': 'اختر تاريخ البدء.',
  'offers.err.endRequired': 'اختر تاريخ الانتهاء.',
  'offers.err.startPast': 'لا يمكن أن يبدأ العرض قبل اليوم.',
  'offers.err.endBeforeStart': 'يجب أن يكون تاريخ الانتهاء بعد تاريخ البدء.',
  'offers.err.window': 'تصل مدة العرض إلى 90 يومًا كحد أقصى.',
  'offers.err.percentRange': 'يجب أن يكون الخصم بين 5٪ و70٪.',
  'offers.err.fixedMin': 'يجب ألا يقل الخصم الثابت عن 5 دراهم.',
  'offers.err.exceedsPrice': 'يجب أن يكون الخصم الثابت أقل من سعر أرخص خدمة مشمولة.',
  'offers.err.maxPerBookingMin': 'أدخل 5 دراهم على الأقل، أو اتركه فارغًا.',
  'offers.err.maxBookingsRange': 'أدخل عددًا صحيحًا من 1 إلى 10,000، أو اتركه فارغًا.',
  'offers.err.noService': 'اختر خدمة نشطة واحدة على الأقل.',
  'offers.err.bannerRequired': 'أضف صورة للعرض (JPG أو PNG أو WebP، حتى 5 ميغابايت).',
  'offers.err.contactDetails': 'لا يُسمح ببيانات التواصل أو الروابط في العروض.',
  'offers.err.arabicRequired': 'العنوان بالعربية مطلوب.',
  'offers.err.maxBelowUsed': 'لا يمكن أن يكون الحد الأقصى للحجوزات أقل من الحجوزات المستخدمة.',
  'offers.err.garageBlocked': 'يجب أن يكون كراجك معتمدًا ونشطًا لتشغيل العروض.',
  'offers.err.transition': 'لا يمكن تنفيذ هذا الإجراء في حالة العرض الحالية. أعد التحميل لرؤية آخر تحديث.',
  'offers.err.stateChanged': 'تغيّر هذا العرض، أعد التحميل وحاول مرة أخرى.',
  'offers.err.ended': 'انتهى هذا العرض بالفعل.',
  'offers.err.featureDisabled': 'العروض متوقفة مؤقتًا.',
  'offers.err.storageKey': 'تعذّر التحقق من الصورة المرفوعة. ارفعها مرة أخرى.',
  'offers.err.generic': 'حدث خطأ ما. حاول مرة أخرى.',
  'offers.err.reload': 'إعادة التحميل',
  'offers.err.fixBelow': 'يرجى تصحيح الحقول المحددة.',
  'offers.priceGuard.message': "هذا السعر مشمول بالعرض '{title}' ({off}). الحد الأدنى للسعر أثناء سريان العرض: {min} درهم. أنهِ العرض أو عدّله أولاً.",
  'offers.priceGuard.offFixed': 'خصم {amount} درهم',
  'offers.priceGuard.offPercent': 'خصم {pct}٪',
};

export const OFFERS_DICT: Record<'en' | 'ar', Dict> = { en, ar };

export type OffersT = (key: string, vars?: Record<string, string | number>) => string;

function interpolate(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** `t` for the offers screens: DB translation first, then the bundled EN/AR string. */
export function useOffersT(): { t: OffersT; lang: 'en' | 'ar'; dir: 'ltr' | 'rtl' } {
  const { t: dbT, lang, dir } = useLanguage();
  const t = useCallback<OffersT>((key, vars) => {
    const fromDb = dbT(key);
    const text = fromDb && fromDb !== key ? fromDb : (OFFERS_DICT[lang]?.[key] ?? OFFERS_DICT.en[key] ?? key);
    return interpolate(text, vars);
  }, [dbT, lang]);
  return { t, lang, dir };
}
